import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton, 
  Popover, 
  Typography, 
  Box, 
  Divider,
  Paper,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { trackEvent } from '../../utils/analytics';
import { detectLanguageFromPathname } from '../../i18n';
import { useLocation } from 'react-router-dom';

const AnalysisInfoButton = ({ analysisType, place = 'default' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const language = detectLanguageFromPathname(location.pathname);
  const isEnglish = language === 'en';
  const predictionMeta = null;
  const selectedLocation = 'default';

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    trackEvent('view_analysis_info', {
      analysis_type: analysisType,
      place,
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'analysis-info-popover' : undefined;

  // 場所に応じた混雑度境界値
  const getThresholds = () => {
    const currentPlace = place !== 'default' ? place : selectedLocation;
    if (predictionMeta?.threshold_min != null && predictionMeta?.threshold_max != null) {
      return [Number(predictionMeta.threshold_min), Number(predictionMeta.threshold_max)];
    }
    const thresholds = {
      'honmachi2': analysisType === 'calendar' ? [2300, 9500] : [65, 850],
      'honmachi3': analysisType === 'calendar' ? [1500, 8500] : [35, 400],
      'honmachi4': analysisType === 'calendar' ? [1000, 10000] : [50, 2000],
      'jinnya': analysisType === 'calendar' ? [700, 7000] : [50, 400],
      'kokubunjidori': analysisType === 'calendar' ? [1600, 5800] : [40, 450],
      'nakabashi': analysisType === 'calendar' ? [1600, 7800] : [40, 650],
      'omotesando': analysisType === 'calendar' ? [700, 7000] : [25, 700],
      'yasukawadori': analysisType === 'calendar' ? [7000, 26000] : [400, 1950],
      'yottekan': analysisType === 'calendar' ? [300, 3500] : [10, 300],
      'default': analysisType === 'calendar' ? [2300, 9500] : [10, 500]
    };
    
    return thresholds[currentPlace] || thresholds['default'];
  };

  // 分析タイプに応じた情報を取得
  const getAnalysisInfo = () => {
    const thresholds = getThresholds();
    const thresholdNotes = [
      isEnglish
        ? `• Congestion 1-2 threshold: around ${thresholds[0]} people`
        : `• 混雑度1-2の境界値: ${thresholds[0]}人付近`,
      isEnglish
        ? '• Congestion 10-11 threshold: around the average pedestrian count'
        : '• 混雑度10-11の境界値: 歩行者数の平均値付近',
      isEnglish
        ? `• Congestion 19-20 threshold: around ${thresholds[1]} people`
        : `• 混雑度19-20の境界値: ${thresholds[1]}人付近`,
    ];

    const commonNotes = [
      isEnglish
        ? '• Congestion is shown on a 1-20 scale (higher means busier).'
        : '• 混雑度は1〜20（数値が大きいほど混雑）で表示します。',
      isEnglish
        ? '• From today onward, values are forecasts. They may differ from actual congestion.'
        : '• 本日以降の情報は予測値です。実際の混雑と差が出る場合があります。',
    ];
    if (predictionMeta?.threshold_updated_at) {
      commonNotes.unshift(
        isEnglish
          ? `• Applied thresholds updated at: ${predictionMeta.threshold_updated_at}`
          : `• 適用中の閾値更新日時: ${predictionMeta.threshold_updated_at}`
      );
    }

    const infoMap = {
      calendar: {
        title: isEnglish ? 'Calendar Congestion' : '混雑度カレンダーの計算方法',
        description: [
          isEnglish
            ? 'Daily totals from 09:00–19:00 pedestrian counts, mapped to a 1–20 congestion scale (same window as TV weekly).'
            : '9–19時の歩行者数の日合計から混雑度（1〜20）を算出します（TV週間・放送資料と同じ時間帯）。',
        ],
      },
      dateTime: {
        title: isEnglish ? 'Date x Time Congestion' : '日付×時間の混雑度の計算方法',
        description: [isEnglish ? 'Calculated from pedestrian counts for each date and hour.' : '各日・各時間の歩行者数から混雑度を算出します。'],
      },
      weekTime: {
        title: isEnglish ? 'Weekday x Time Congestion' : '曜日×時間の混雑度の計算方法',
        description: [isEnglish ? 'Calculated by averaging the same hour across weekdays.' : '曜日ごとの同時刻データを集約して混雑度を算出します。'],
      },
      monthTrend: {
        title: isEnglish ? 'Monthly Trend' : '月ごとの傾向の見方',
        description: [isEnglish ? 'Monthly aggregated congestion trend.' : '月単位で集約した混雑度の傾向です。'],
      },
      weekTrend: {
        title: isEnglish ? 'Weekly Trend' : '週ごとの傾向の見方',
        description: [isEnglish ? 'Weekly aggregated congestion trend.' : '週単位で集約した混雑度の傾向です。'],
      },
      yearTrend: {
        title: isEnglish ? 'Yearly Trend' : '年ごとの傾向の見方',
        description: [isEnglish ? 'Yearly aggregated congestion trend.' : '年単位で集約した混雑度の傾向です。'],
      },
    };

    const current = infoMap[analysisType] || {
      title: isEnglish ? 'Analysis Method' : '分析方法',
      description: [isEnglish ? 'Shows how this chart is calculated.' : 'このグラフの分析方法を表示します。'],
    };

    return {
      ...current,
      details: [...thresholdNotes, ...commonNotes],
    };
  };

  const info = getAnalysisInfo();

  return (
    <>
      <Tooltip title={isEnglish ? 'About analysis method' : '分析方法について'}>
        <IconButton 
          aria-describedby={id} 
          onClick={handleClick} 
          size="small" 
          sx={{ 
            color: 'text.secondary',
            opacity: 0.7,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff', // 明示的に白背景を指定
            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <Paper sx={{ 
          p: 2, 
          maxWidth: 300,
          backgroundColor: '#ffffff',  // 明示的に白背景を指定
          color: '#333333'  // テキストの色を暗めの色に設定
        }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
            {info.title}
          </Typography>
          
          <Divider sx={{ my: 1 }} />
          
          <Box sx={{ mb: 1.5 }}>
            {info.description.map((text, index) => (
              <Typography 
                key={index} 
                variant="body2" 
                sx={{ 
                  mb: 0.5,
                  color: '#333333'  // テキストの色を明示的に設定
                }}
              >
                {text}
              </Typography>
            ))}
          </Box>
          
          {info.details.length > 0 && (
            <>
              <Box sx={{ mt: 1 }}>
                {info.details.map((text, index) => (
                  <Typography 
                    key={index} 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.85rem',
                      mb: 0.5,
                      color: '#333333',  // テキストの色を明示的に設定
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',  // 境界値の背景をわずかに強調
                      padding: '2px 4px',
                      borderRadius: '3px'
                    }}
                  >
                    {text}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </Paper>
      </Popover>
    </>
  );
};

// PropTypesの定義
AnalysisInfoButton.propTypes = {
  analysisType: PropTypes.oneOf(['calendar', 'dateTime', 'weekTime', 'monthTrend', 'weekTrend', 'yearTrend']).isRequired,
  place: PropTypes.string
};



export default AnalysisInfoButton;
